import type { PartDiagnosticStatus, PartStatus } from '@/types/rc/miners/PartStatus'
import { IndividualPart, PartType } from '@/types/rc/miners/PartType'
import type { IService } from '../work-orders'
import type { MinerStatus } from './MinerStatus'

export interface BulkMinersResult {
  success: boolean
  errors: string[]
}

export interface IMinerConditions {
  diagnosed: boolean
  diagnostic?: IMinerDiagnostic | null
  last_stress_test?: IStressTest | null
  stress_tested: boolean
}

export interface IRepairAuthorization {
  id: number
  granted: boolean | null
  lowest_cost: string
  highest_cost: string
  created_at: string
  granted_at: string | null
  granted_by: string | null
}

export interface IRepairAction {
  id: number
  part_type: string
  action: string
  price: number
}

export interface IRepairItem {
  id: number
  part_type: string
  description: string
}

export interface IFailure {
  id: number
  failure_type: IFailureType
  executed_repair_action: IRepairAction | null
  repair_authorization: IRepairAuthorization | null
  repair_items: IRepairItem[]
  part_type: PartType
  comment: string | null
  pictures: {
    id: number
    picture_url: string
  }[]

  // Used only to update part status after a repair updates it, check updateFailureMutation's definition (onSuccess callback):
  part?: IBasePart
}

export interface IFailureType {
  id: number
  part_type: PartType
  description: string
}

export interface IModel {
  id?: number
  name: string | null
  version: string | null
  valueKey?: string
}

export interface IBasePart {
  id: number
  sn: string | null
  pcb_id: string | null
  status: PartStatus | null
  initial_diagnostic: PartDiagnosticStatus | null
  model: string | null
  model_version: string | null
  repair_notes: string
  has_been_in_rc_before: boolean
  moved_to_inventory: boolean
}

export interface IPowerSupply extends IBasePart {} // eslint-disable-line @typescript-eslint/no-empty-object-type

export interface IControlBoard extends IBasePart {
  mac_address: string
}

export interface IHashboard extends IBasePart {
  number: number
}

export interface IFan extends IBasePart {
  number: number
}

export interface IMinerCustomer {
  id: number
  name: string
}

export interface IMinerIdentifier {
  id: number
  model: string
  sn: string
  asset_tag: string | null
  status: MinerStatus
  oem_warranty_expiration_date: string | null
  mac_address: string | null
}

export interface IMinerDetails extends IMinerIdentifier {
  condition: IMinerConditions
  psu: IPowerSupply | null
  cb: IControlBoard | null
  hb_model: string | null
  hashboards: IHashboard[]
  fans: IFan[]
  repair_notes: string | null
  damages: any[]
  work_order_number: string
  customer: IMinerCustomer
  performed_services: IService[]
  has_been_in_rc_before: boolean
  inbound_container: {
    id: number
    number: string
  } | null
  outbound_container: {
    id: number
    number: string
  } | null
  hashboard_only: boolean
}

export type IMinerDetailsLite = Omit<IMinerDetails, 'damages' | 'work_order_number' | 'customer'>

//*******************************************************/

// Catch-all for IMinerDetails part
// Parts in the IMinerDetails response might have failure component

// Actually this would be, I believe:
// type IDetailsPart = (IPowerSupply | IControlBoard | IHashboard | IFan) & {
//   failure: IFailure
//   replaced_components: any[]
// }
// But this introduces its own type-gymnastics

export interface IReplacementSparePart {
  id: number
  owner: string
}

export interface IDetailsPart extends IBasePart {
  mac_address?: string
  number?: number
  failure?: IFailure
  replaced_components: IComponent[]
  replacement_spare_part?: IReplacementSparePart
  damages: IPartDamage[]
}

//*******************************************************/

export interface IMinerCardElement {
  type: string
  model: string | null
  sn: string | null
  status: MinerStatus | null
}

export interface IMinerEntryCommons {
  id: string
  sn: string
  model: string
  mac_address: string
  psu_model: string
  psu_sn: string
  cb_model: string
  cb_sn: string
  customer: string
  work_order_number: string
}

export interface IMinerEntry extends IMinerEntryCommons {
  hashboards: IHashboard[]
  fans: IFan[]
}

export interface IMinerModel {
  family: string
  family_abbreviation: string
  name: string
  manufacturer: string
}

export interface IMinerHistory {
  status: MinerStatus | null
  part_name: string
  part_sn: string | null
  part_model: string | null
  part_status: PartStatus | null
  action: string
  action_user: string
  action_timestamp: string
}

export enum DiagnosticResult {
  PASS = 'Pass',
  FAIL = 'Fail',
}

export interface IMinerDiagnostic {
  id: number
  files: { id: number; file_url: string; name: string }[]
  date: string
  result: DiagnosticResult
  failure_reasons: string
}

// As returned by the API
export interface IStressTest {
  id?: number
  date: string
  result: DiagnosticResult
  failure_reasons: string
  files: { id: number; file_url: string; name: string }[]
}

export const minerDetailsParts: IndividualPart[] = [
  IndividualPart.PSU,
  IndividualPart.CB,
  IndividualPart.FAN1,
  IndividualPart.FAN2,
  IndividualPart.FAN3,
  IndividualPart.FAN4,
  IndividualPart.HB0,
  IndividualPart.HB1,
  IndividualPart.HB2,
  IndividualPart.HB3,
]

export const HashboardParts: IndividualPart[] = [
  IndividualPart.HB0,
  IndividualPart.HB1,
  IndividualPart.HB2,
  IndividualPart.HB3,
]

export interface IComponentType {
  name: string
  display_name: string
}

export interface IComponent {
  id: number
  external_id: number | null
  pcb_id: string | null
  model_number: string | null
  type: string
  display_name: string
}

export enum PartDamageType {
  Water = 'Water',
  Physical = 'Physical',
  Overcurrent = 'Overcurrent',
  Other = 'Other',
}

export interface IPartDamage {
  id?: number
  type: PartDamageType
  description: string
  pictures: { picture_url: string }[]
}
